import React, { useCallback, useEffect } from 'react';
import { styled } from '@compiled/react';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';

import type { SupportedThirdParty } from '../../../../common/constants/import-type';
import type { RequestAccessTokenMethod } from '../../../../common/types';
import { SiteName } from '../../../../common/ui/site-name';
import { useContentForThirdParty } from '../../../../common/utils';
import { useThirdPartyState } from '../../../../controllers/import-state';
import { useThirdPartySiteName } from '../../../../services/third-party-site-name';
import { EditPersonalAccessToken } from './edit-personal-access-token';

export type AccountInfoProps = {
	thirdParty: SupportedThirdParty;
	shouldShowAccountInfoIfUsingPAT: boolean;
	requestAuthenticationToken: RequestAccessTokenMethod;
};

export const AccountInfo = ({
	thirdParty,
	shouldShowAccountInfoIfUsingPAT,
	requestAuthenticationToken,
}: AccountInfoProps) => {
	const { getSiteName, isLoading, hasErrorOccurred } = useThirdPartySiteName({
		thirdParty,
		requestAuthenticationToken,
	});
	const { loadingSiteNameErrorOccurredDescription } = useContentForThirdParty(thirdParty);
	const [{ siteName }, { setThirdPartyState }] = useThirdPartyState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const onFetchSiteName = useCallback(() => {
		getSiteName().then((data) => {
			setThirdPartyState({ siteName: data });
			fireUIAnalytics(createAnalyticsEvent({}), 'thirdPartySiteName retrieved', {
				thirdParty,
				siteName,
			});
		});
	}, [createAnalyticsEvent, getSiteName, setThirdPartyState, siteName, thirdParty]);

	useEffect(() => {
		shouldShowAccountInfoIfUsingPAT && !siteName && onFetchSiteName();
	}, [siteName, onFetchSiteName, shouldShowAccountInfoIfUsingPAT]);

	return (
		<>
			{shouldShowAccountInfoIfUsingPAT && (
				<SiteName
					siteName={siteName}
					onFetchSiteName={onFetchSiteName}
					isLoading={isLoading}
					hasErrorOccurred={hasErrorOccurred}
				/>
			)}
			<EditPersonalAccessToken thirdParty={thirdParty} />
			{hasErrorOccurred && loadingSiteNameErrorOccurredDescription && (
				<ErrorWrapper>
					<ErrorIcon label="" size="medium" primaryColor={token('color.icon.danger', '#EF5C48')} />
					<ErrorMessageWrapper>
						{formatMessage(loadingSiteNameErrorOccurredDescription)}
					</ErrorMessageWrapper>
				</ErrorWrapper>
			)}
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorMessageWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.danger', colors.R400),
	fontSize: '12px',
	marginTop: token('space.050', '4px'),
});
